import React, { useCallback, useContext, useState } from "react";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";
import { useBlocks, useUi } from "@jelly/lesson";
import { Accordion, AccordionItem } from "@src/lms/jelly/extensions/ui";
import { useIntl } from "react-intl";

import hearCandy from "@src/assets/images/hear-candy-logo.png";
import { messages } from "./Case1ArtisticPov.messages";
import styles from "./Case1ArtisticPov.module.scss";

import {
  LiveConcertDiagram,
  LiveConcertItem,
} from "@src/lms/jelly/extensions/layouts/_lesson/case7/artistic-pov/LiveConcertDiagram";
import { AccordionContext } from "@src/lms/jelly/extensions/ui/accordion/AccordionContext";
import { AudioPlayerBlock } from "@src/lms/jelly/extensions/blocks/audioPlayer/AudioPlayerBlock";
import * as appConfig from "appConfig";
import { BioNoteModal } from "@src/lms/jelly/extensions/layouts/_lesson/case7/artistic-pov/components/BioNoteModal";
import { BuffModal } from "@src/lms/jelly/extensions/layouts/_lesson/case7/artistic-pov/components/BuffModal";

interface Case1ArtisticPov {
  title: string;
}

const ACCORDION_IDS = {
  marlowe: "marlowe",
  buff: "buff",
  recordings: "recordings",
} as const;

const DIAGRAM_ITEM_TO_ACCORDION_ITEM_MAPPING: Record<
  LiveConcertItem,
  (typeof ACCORDION_IDS)[keyof typeof ACCORDION_IDS]
> = {
  marlowe: ACCORDION_IDS.marlowe,
  skal: ACCORDION_IDS.marlowe,
  laura: ACCORDION_IDS.marlowe,
  buff: ACCORDION_IDS.buff,
  recordings: ACCORDION_IDS.recordings,
};

export const Case1ArtisticPov: React.FC<Case1ArtisticPov> = ({ title }) => {
  return (
    <Accordion allowMultiOpen={false} defaultOpen={[ACCORDION_IDS.marlowe]}>
      <Case1ArtisticPovContent title={title} />
    </Accordion>
  );
};

const Case1ArtisticPovContent: React.FC<Case1ArtisticPov> = ({ title }) => {
  const ui = useUi();
  const blocks = useBlocks();
  const { formatMessage } = useIntl();
  const [modalOpen, setModalOpen] = useState(false);
  const [buffModalOpen, setBuffModalOpen] = useState(false);

  const handleModalToggle = useCallback(() => setModalOpen((open) => !open), []);
  const handleBuffModalToggle = useCallback(() => setBuffModalOpen((open) => !open), []);

  const accordion = (
    <div className={styles.accordion}>
      <ui.Scrollbars>
        <AccordionItem
          id={ACCORDION_IDS.marlowe}
          title={formatMessage(messages.accordionItem1Title)}
          unmountChildrenWhenHidden
        >
          {/*//TODO replace video*/}
          {/*//TODO: Check if we can add placeholder*/}
          <div style={{ width: "100%", height: "142px" }}>
            <blocks.video src={"https://youtu.be/JVc-aQ8Vem0"} provider={"youtube"} />
          </div>
          <p>{formatMessage(messages.accordionItem1Description)}</p>
          <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleModalToggle}>
            {formatMessage(messages.bioNote)}
          </ui.Button>
        </AccordionItem>
        <AccordionItem
          id={ACCORDION_IDS.buff}
          title={formatMessage(messages.accordionItem2Title)}
          unmountChildrenWhenHidden
        >
          {/*//TODO replace video*/}
          {/*//TODO: Check if we can add placeholder*/}
          <div style={{ width: "100%", height: "142px" }}>
            <blocks.video src={"https://www.youtube.com/watch?v=_U0dEK1R2u0"} provider={"youtube"} />
          </div>
          <p>{formatMessage(messages.accordionItem2Description)}</p>
          <ui.Button shape={"normal"} theme={"primary"} size={"small"} onClick={handleBuffModalToggle}>
            {formatMessage(messages.bioNote)}
          </ui.Button>
        </AccordionItem>
        <AccordionItem
          id={ACCORDION_IDS.recordings}
          title={formatMessage(messages.accordionItem3Title)}
          preventPrerender
          unmountChildrenWhenHidden
        >
          {/*//TODO: Replace src to minified version once received*/}
          <AudioPlayerBlock
            autoplayOnTrackSwitch
            samples={[
              {
                src: `${appConfig.STATIC_ASSETS_URL}/lms/case-example/artistic-pov/3d-microphones-fmf-ambeo.wav`,
                title: `0:20 - "Echoes of Space" (Ambeo)`,
              },
              {
                src: `${appConfig.STATIC_ASSETS_URL}/lms/case-example/artistic-pov/3d-microphones-fmf-zoom-vr.wav`,
                title: `0:35 - "Echoes of Space" (Zoom)`,
              },
              {
                src: `${appConfig.STATIC_ASSETS_URL}/lms/case-example/artistic-pov/3d-microphones-fmf-zylia.wav`,
                title: `0:19 - "Echoes of Space" (Zylia)`,
              },
            ]}
          />
          <div className={styles.collaboration}>
            {formatMessage(messages.collaboration)}
            <img src={hearCandy} alt="hear candy" />
          </div>
          {formatMessage(messages.accordionItem3Description)}
        </AccordionItem>
      </ui.Scrollbars>
    </div>
  );

  return (
    <StandardCase title={title} leftComponent={accordion}>
      <div className={styles.diagramWrapper}>
        <AccordionWiseDiagram />
      </div>
      <BioNoteModal visible={modalOpen} onClose={handleModalToggle} />
      <BuffModal visible={buffModalOpen} onClose={handleBuffModalToggle} />
    </StandardCase>
  );
};

const AccordionWiseDiagram: React.FC = () => {
  const context = useContext(AccordionContext);

  const activeItems = context.openIds.flatMap((item) => {
    return Object.entries(DIAGRAM_ITEM_TO_ACCORDION_ITEM_MAPPING)
      .filter(([, accordionItem]) => accordionItem === item)
      .map(([diagramItem]) => diagramItem as LiveConcertItem);
  });

  const handleItemSelect = useCallback(
    (diagramItem: LiveConcertItem): void => {
      const accordionItem = DIAGRAM_ITEM_TO_ACCORDION_ITEM_MAPPING[diagramItem];
      context.onToggle(accordionItem, context.openIds.includes(accordionItem));
    },
    [context]
  );

  return <LiveConcertDiagram activeItems={activeItems} onItemSelect={handleItemSelect} />;
};
